import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { WorkbookView } from '~/components/workbook/workbook-view';
import { useTranslator } from '~/lib/i18n';
import type { Workbook } from '~/lib/workbook/schema';
import { getWorkbook, saveWorkbook } from '~/lib/workbook/storage';
import { decodeWorkbookFromUrlFragment } from '~/lib/workbook/url-codec';

export const Route = createFileRoute('/workbook/$id')({
  component: WorkbookRoute,
});

function WorkbookRoute() {
  const { id } = Route.useParams();
  const t = useTranslator();
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; workbook: Workbook } | { status: 'missing' }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (typeof window === 'undefined') return;

      if (window.location.hash.startsWith('#w=')) {
        try {
          const wb = await decodeWorkbookFromUrlFragment(window.location.hash);
          if (wb && !cancelled) {
            saveWorkbook(wb);
            setState({ status: 'ready', workbook: wb });
            return;
          }
        } catch {
          // fall through to localStorage
        }
      }

      const local = getWorkbook(id);
      if (cancelled) return;
      if (local) {
        setState({ status: 'ready', workbook: local });
      } else {
        setState({ status: 'missing' });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.status === 'loading') {
    return (
      <main style={loadingStyle}>
        <p>{t('workbook.loading')}</p>
      </main>
    );
  }
  if (state.status === 'missing') {
    return (
      <main style={loadingStyle}>
        <h2>{t('workbook.missing.title')}</h2>
        <p>{t('workbook.missing.body', { id })}</p>
        <a href="/">{t('workbook.missing.cta')}</a>
      </main>
    );
  }
  return <WorkbookView workbook={state.workbook} />;
}

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '4rem 2rem',
  alignItems: 'center',
  fontFamily: 'system-ui, sans-serif',
};
